/**
 * The called-ball display: the latest number large, the previous few small.
 *
 * Sits above the card because that is the order a player's attention moves —
 * hear the call, then look down at the cartela. Showing only the last few
 * rather than all 75 is deliberate: the full board is a verification tool, not
 * a playing aid, and a wall of numbers competes with the card for exactly the
 * attention the card needs.
 */

import { memo } from 'react';
import { ballLetter } from '../format.ts';

interface BallCallProps {
  /** In draw order, oldest first. */
  calledNumbers: readonly number[];
  /** How many of the preceding calls to show. */
  recentCount?: number;
}

export const BallCall = memo(function BallCall({
  calledNumbers,
  recentCount = 5,
}: BallCallProps) {
  const last = calledNumbers.at(-1) ?? null;
  // Newest first: the eye starts at the left, and the most recent is the one
  // that matters most.
  const recent = calledNumbers.slice(-recentCount - 1, -1).reverse();

  return (
    <div className="ballcall">
      <div className="ballcall__current">
        {last === null ? (
          <div className="ball ball--empty" aria-hidden="true">
            <span className="ball__dash">—</span>
          </div>
        ) : (
          // `key` on the number forces a remount per draw, so the entrance
          // animation replays instead of the DOM node being quietly reused.
          <div className="ball ball--current" key={last} aria-live="polite">
            <span className="ball__letter">{ballLetter(last)}</span>
            <span className="ball__number">{last}</span>
          </div>
        )}
      </div>

      {recent.length > 0 && (
        <div className="ballcall__recent" aria-hidden="true">
          {recent.map((n) => (
            <span key={n} className="ball ball--small">
              {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
});
